import React from 'react';
import { Modal, Button, Row, Col, Badge, Card, Spinner, Table } from 'react-bootstrap';
import { FaFileAlt, FaFilePdf, FaFileImage, FaEye, FaDownload } from 'react-icons/fa'; 
import useEmployeeDocuments from '../../hooks/useEmployeeDocuments'; 
import {
    getEmployeeStatusColor,
    getEmployeeStatusDisplayName,
    getContractTypeDisplayName,
    getDepartmentDisplayName,
    formatTurkishDate,
    formatCurrency
} from '../../services/api';

const EmployeeDetailModal = ({
    showDetailModal,
    setShowDetailModal,
    selectedEmployee,
    handleEditClick
}) => {
    console.log('🚪 MODAL RENDER', showDetailModal);
    console.log('👤 EMPLOYEE ID', selectedEmployee?._id);
    console.log('📛 EMPLOYEE NAME', selectedEmployee?.personalInfo?.fullName);
    console.log('⏰ RENDER TIME', Date.now());
    // Document management hook -  
    const {
        documents,
        loading: documentsLoading,
        getDocumentStats,
        downloadDocument,
        error,
        categories,
        forceRefresh 
    } = useEmployeeDocuments(selectedEmployee?._id);

    // ✅ Force refresh when modal opens
    React.useEffect(() => {
        if (showDetailModal && selectedEmployee?._id) {
            forceRefresh();
        }
    }, [showDetailModal, selectedEmployee?._id, forceRefresh]);

    // Helper functions -  
    const getFileIcon = (mimeType) => {
        if (mimeType === 'application/pdf') return FaFilePdf;
        if (mimeType?.startsWith('image/')) return FaFileImage;
        return FaFileAlt;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const handleViewDocument = (document) => {
        const viewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(document.url)}&embedded=true`;
        window.open(viewUrl, '_blank');
    };

    const stats = getDocumentStats();

    return (
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>👁️ Personel Detayları</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {selectedEmployee && (
                    <Row>
                        <Col md={6}>
                            <h6>Kişisel Bilgiler</h6>
                            <p><strong>Ad Soyad:</strong> {selectedEmployee.personalInfo?.fullName}</p>
                            <p><strong>TC Kimlik:</strong> {selectedEmployee.personalInfo?.tcNo}</p>
                            <p><strong>Telefon:</strong> {selectedEmployee.personalInfo?.phone}</p>
                            <p><strong>Email:</strong> {selectedEmployee.personalInfo?.email}</p>
                            <p><strong>Doğum Tarihi:</strong> {formatTurkishDate(selectedEmployee.personalInfo?.birthDate)}</p>
                            <p><strong>Adres:</strong> {selectedEmployee.personalInfo?.address}</p>
                            <p><strong>IBAN Sahibi İsmi:</strong> {selectedEmployee.personalInfo?.ibanHolder || 'Belirtilmemiş'}</p>
                            <p><strong>IBAN Numarası:</strong> {selectedEmployee.personalInfo?.ibanNumber || 'Belirtilmemiş'}</p>

                            <h6 className="mt-4">Acil Durum İletişim</h6>
                            <p><strong>Ad:</strong> {selectedEmployee.personalInfo?.emergencyContact?.name}</p>
                            <p><strong>Telefon:</strong> {selectedEmployee.personalInfo?.emergencyContact?.phone}</p>
                            <p><strong>Yakınlık:</strong> {selectedEmployee.personalInfo?.emergencyContact?.relation}</p>
                        </Col>
                        <Col md={6}>
                            <h6>İş Bilgileri</h6>
                            <p><strong>Pozisyon:</strong> {selectedEmployee.workInfo?.position}</p>
                            <p><strong>Departman:</strong> {getDepartmentDisplayName(selectedEmployee.workInfo?.department)}</p>
                            <p><strong>İşe Başlama:</strong> {formatTurkishDate(selectedEmployee.workInfo?.startDate)}</p>
                            <p><strong>Sözleşme Türü:</strong> {getContractTypeDisplayName(selectedEmployee.workInfo?.contractType)}</p>
                            {selectedEmployee.workInfo?.contractEndDate && (
                                <p><strong>Sözleşme Bitiş:</strong> {formatTurkishDate(selectedEmployee.workInfo.contractEndDate)}</p>
                            )}
                            <p><strong>Maaş:</strong> {formatCurrency(selectedEmployee.workInfo?.salary)}</p>
                            <p><strong>İş Durumu:</strong>
                                <Badge bg={getEmployeeStatusColor(selectedEmployee.workInfo?.workStatus)} className="ms-2">
                                    {getEmployeeStatusDisplayName(selectedEmployee.workInfo?.workStatus)}
                                </Badge>
                            </p>

                            <h6 className="mt-4">İzin Hakları</h6>
                            <p><strong>Yıllık İzin:</strong> {selectedEmployee.leaveRights?.annual || 0} gün</p>
                            <p><strong>Kullanılan:</strong> {selectedEmployee.leaveRights?.used || 0} gün</p>
                            <p><strong>Kalan:</strong> {selectedEmployee.leaveRights?.remaining || 0} gün</p>
                        </Col>
                    </Row>
                )}

                {/* BELGELER BÖLÜMÜ -   */}
                {selectedEmployee && (
                    <>
                        <hr className="my-4" />
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">📎 Personel Belgeleri</h6>
                                <div className="d-flex gap-3">
                                    <Badge bg="primary">{stats.total} Toplam</Badge>
                                    <Badge bg="success">{stats.requiredComplete}/{stats.requiredTotal} Zorunlu</Badge>
                                    <Badge bg="info">{stats.completionPercentage}% Tamamlama</Badge>
                                </div>
                            </div>

                            {documentsLoading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" size="sm" />
                                    <span className="ms-2">Belgeler yükleniyor...</span>
                                </div>
                            ) : documents.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table striped hover className="mb-0">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>Kategori</th>
                                                    <th>Dosya Adı</th>
                                                    <th>Yükleme Tarihi</th>
                                                    <th>Yükleyen</th>
                                                    <th width="120">İşlemler</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {documents.map((document) => {
                                                    const category = categories.find(c => c.id === document.category);

                                                    return (
                                                        <tr key={document._id}>
                                                            <td>
                                                                <Badge bg={category?.color || 'secondary'} className="px-2 py-1">
                                                                    {category?.label || document.category}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-truncate" style={{ maxWidth: '200px' }}>
                                                                <span title={document.originalName}>
                                                                    {document.originalName}
                                                                </span>
                                                                <br />
                                                                <small className="text-muted">{formatFileSize(document.fileSize)}</small>
                                                            </td>
                                                            <td>
                                                                <small>{new Date(document.uploadDate).toLocaleDateString('tr-TR')}</small>
                                                            </td>
                                                            <td>
                                                                <small className="text-muted">
                                                                    {document.uploadedBy?.name || 'Bilinmiyor'}
                                                                </small>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-1">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-primary"
                                                                        onClick={() => handleViewDocument(document)}
                                                                        title="Görüntüle"
                                                                    >
                                                                        <FaEye size={12} />
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-success"
                                                                        onClick={() => downloadDocument(document)}
                                                                        title="İndir"
                                                                    >
                                                                        <FaDownload size={12} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    <FaFileAlt className="mb-2" size={32} />
                                    <p className="mb-0">Henüz belge yüklenmemiş</p>
                                    <small>Personeli düzenleyerek belge ekleyebilirsiniz</small>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                    Kapat
                </Button>
                {selectedEmployee && (
                    <Button
                        variant="primary"
                        onClick={() => {
                            setShowDetailModal(false);
                            handleEditClick(selectedEmployee);
                        }}
                    >
                        Düzenle
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default EmployeeDetailModal;